/**
 * The evaluator's fit label, as every surface that still shows GTM Fit renders it.
 *
 * GTM Fit is deprecated in favour of Account Priority (#4364), but only for the
 * workspaces that are on that rollout — everywhere else it is still the account
 * score, now on the audience contacts grid as well as in Prospects. The palette
 * and the label formatting live here rather than inside the Prospects drawer so a
 * component outside that view can render the same label without importing a view.
 *
 * Note this vocabulary is NOT the High/Medium/Low band the backend publishes for
 * Account Priority: it is the raw evaluator enum on no fixed threshold, which is
 * why it qualifies a score rather than colouring one.
 */

/** The evaluator's `ai_fit_label` enum. Every other fit-label list in the app is a view of this one. */
export const GTM_FIT_LABELS = ['ideal', 'hot_opportunity', 'good', 'warm_lead', 'weak', 'poor', 'no_fit'] as const;
export type GtmFitLabel = typeof GTM_FIT_LABELS[number];

/** Labels that earn a row in Tasks by default ("high-value" in the migration-121 enum notes). */
export const HIGH_VALUE_FIT_LABELS = ['ideal', 'good', 'hot_opportunity', 'warm_lead'] as const satisfies readonly GtmFitLabel[];

/** Not evaluator output: Chrome-extension tasks carry this in the fit-label field. */
export const MANUAL_FIT_LABEL = 'manual';
/** Legacy evaluator value, still present on old rows. */
export const DISQUALIFIED_FIT_LABEL = 'disqualified';

/** Everything the Tasks fit-label filter can send. */
export type TaskFitLabel = GtmFitLabel | typeof MANUAL_FIT_LABEL | typeof DISQUALIFIED_FIT_LABEL;

export const FIT_LABEL_COLORS: Record<string, { bg: string; fg: string }> = {
  ideal: { bg: '#E0E7FF', fg: '#3730A3' },
  hot_opportunity: { bg: '#FFEDD5', fg: '#B45309' },
  good: { bg: '#E0E7FF', fg: '#3730A3' },
  warm_lead: { bg: '#FEF3C7', fg: '#92400E' },
  weak: { bg: '#FEE2E2', fg: '#B91C1C' },
  poor: { bg: '#FEE2E2', fg: '#B91C1C' },
  no_fit: { bg: '#F1F5F9', fg: '#64748B' },
} satisfies Record<GtmFitLabel, { bg: string; fg: string }>;

export const formatFitLabel = (raw: string): string =>
  raw
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
